import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "../assets/monthlyInspectionCreate.module.css";
import propertyMonthlyInspectionsService from "../services/propertyMonthlyInspectionsService";
import PhotoUpload from "../components/PhotoUpload";
import PhotoGallery from "../components/PhotoGallery";
import ChecklistGroup from "../components/ChecklistGroup";
import TicketsTable from "../components/TicketsTable";
import SignatureCanvas from "../components/SignatureCanvas";
import { validateAndShowErrors } from "../utils/validation";
import {
  saveDraft,
  deleteDraft,
  getResumableDraftByProperty,
  OFFLINE_PHOTO_PREFIX,
} from "../lib/offlineDb";
import { triggerSync } from "../lib/inspectionSyncEngine";
import {
  METER_PHOTO_POOL,
  METER_DEFAULT_NAMES,
  DEFAULT_METER_SLOTS,
} from "../constants/meterPhotos";
import { usesNewMeters } from "../utils/meterCutover";
import propertyMetersService from "../services/propertyMetersService";

const MonthlyInspectionCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State management
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [useMockData, setUseMockData] = useState(false); // Always use live data for Monthly Inspection Reports
  const [isMetropolitanHousing, setIsMetropolitanHousing] = useState(false); // MTVH properties have additional photos
  const [ticketTypes, setTicketTypes] = useState([]); // Services for MTVH checklist ticket type dropdown
  const [monthlyInspectionResponses, setMonthlyInspectionResponses] = useState(null); // Canned responses for MTVH notes dropdown

  // Large image viewer state (per-report). Inspection + meter use separate
  // galleries so prev/next stays contained to its section.
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [meterGalleryOpen, setMeterGalleryOpen] = useState(false);
  const [meterGalleryIndex, setMeterGalleryIndex] = useState(0);

  // Rotation state shared between the inline thumbnail (PhotoUpload) and the
  // lightbox. Backend rotates the actual S3 file on submit.
  const [photoRotations, setPhotoRotations] = useState({});
  const rotatePhotoField = (field) => {
    setPhotoRotations((prev) => ({
      ...prev,
      [field]: ((prev[field] || 0) + 90) % 360,
    }));
  };

  // Local data-URL previews for freshly-uploaded photos so the lightbox shows
  // the same image the thumbnail does, without waiting on S3/CDN.
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState({});
  const handlePhotoPreviewReady = useCallback((field, dataUrl) => {
    setPhotoPreviewUrls((prev) => {
      const next = { ...prev };
      if (dataUrl) next[field] = dataUrl;
      else delete next[field];
      return next;
    });
  }, []);

  // Form data
  const [formData, setFormData] = useState({
    status_id: 0, // Unpublished by default
    type_id: 1, // Standard type
    property_id: id,
    monthEnd: 0,
    created_by: 1,

    // Photos - Default names matching Laravel
    image_1: null,
    image_1_a: null,
    image_1_b: null,
    image_1_name: "Front",
    image_2: null,
    image_2_a: null,
    image_2_b: null,
    image_2_name: "Living Space",
    image_3: null,
    image_3_a: null,
    image_3_b: null,
    image_3_name: "Kitchen/Bathroom",
    image_4: null,
    image_4_a: null,
    image_4_b: null,
    image_4_name: "Communal Area",
    image_5: null,
    image_5_name: "Gas Meter 1",
    image_6: null,
    image_6_name: "Gas Meter 2",
    image_7: null,
    image_7_name: "Electric Meter",
    image_8: null,
    image_8_name: "Water Meter",
    image_9: null,
    image_9_name: "",
    image_10: null,
    image_10_name: "",
    image_11: null,
    image_11_name: "",
    image_12: null,
    image_12_name: "",
    image_13: null,
    image_13_name: "",
    image_14: null,
    image_14_name: "",

    // Brief - Default text matching Laravel
    brief_overview: "All of the Guardian rooms have been found to be clean and tidy. Electricity and water is working safely. All facilities are operational. The shower pressure problem has been resolved. Fire routes are clear. We have arranged for our two Head Guardians to patrol the parking area at the front of the building to prevent a build up of rubbish that had been thrown over the hedge and onto the lawns.",

    // Dates - Default inspection date to today matching Laravel
    inspection_date: new Date().toLocaleDateString("en-GB"),
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    lease_start_date: "",

    // Meters (Oct 2026+): reading per property meter, keyed by meter id.
    // Photos ride alongside as `meter_photo_<meterId>` fields.
    meter_readings: {},

    // Meters (legacy, Sep 2026 and earlier) - Default values matching Laravel
    reading_fields: ['Reading Gas', 'Reading Electricity'],
    reading_fields_names: ['Gas', 'Electricity'],
    reading_fields_serials: ['', ''],

    // Other fields
    client: "",
    client_contact: "",
    property_id_name: "",
    address_2: "",
    postcode: "",
    guardians_number: "",
    account_manager: "",
    account_manager_email: "",
    client_contact_short: "",
    address_1: "",
    address_3: "",
    town: "",
    savings_made: "",
    months_under_management: "",

    // Checklist
    external: {},
    kitchen: {},
    living_space: {},
    bathroom: {},
    other: {},
    health_and_safety: {},
    location: {},

    // Signature
    e_sign: "",
  });

  // Dynamic meters
  const [newMeterType, setNewMeterType] = useState("");

  // Which meter photo slots are currently shown (subset of METER_PHOTO_POOL,
  // in order). Defaults to the standard four; extended via "Add photo".
  const [meterSlots, setMeterSlots] = useState(DEFAULT_METER_SLOTS);

  // The property's meter register (Oct 2026+ reports). Meters are managed on
  // the property page; this form only captures a reading and a photo each.
  const [propertyMeters, setPropertyMeters] = useState([]);
  // Distinguishes 'no meters' from 'not fetched yet' -- without it the
  // blocking screen below would flash on every page load.
  const [metersLoaded, setMetersLoaded] = useState(false);
  // The property is marked as genuinely having no meters.
  const [noMetersConfirmed, setNoMetersConfirmed] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await propertyMetersService.getMetersByProperty(id);
        if (!cancelled && response.data?.success) {
          setPropertyMeters(response.data.data || []);
          setNoMetersConfirmed(Boolean(response.data.no_meters_confirmed));
        }
      } catch (error) {
        console.error('Error loading property meters:', error);
      } finally {
        if (!cancelled) setMetersLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Dynamic locations
  const [newLocation, setNewLocation] = useState("");
  const [locations, setLocations] = useState(["Communal area - Location"]);

  // Date picker refs
  const inspectionDateInputRef = useRef(null);
  const leaseStartDateInputRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Offline upload support
  // ---------------------------------------------------------------------------
  const genUuid = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });

  // Identity + buffered photo Blobs for the offline draft. Kept in a ref so
  // capturing a photo doesn't trigger a form-wide re-render.
  const draftRef = useRef({ id: null, clientUuid: null, photos: {} });
  const draftActiveRef = useRef(false); // a draft exists in IndexedDB
  const objectUrlsRef = useRef({}); // restored-preview object URLs to revoke

  // Mirror latest form state into refs so stable callbacks read fresh values.
  const formDataRef = useRef(formData);
  const photoRotationsRef = useRef(photoRotations);
  const locationsRef = useRef(locations);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  useEffect(() => {
    photoRotationsRef.current = photoRotations;
  }, [photoRotations]);

  const ensureDraftId = () => {
    if (!draftRef.current.id) {
      draftRef.current.id = genUuid();
      draftRef.current.clientUuid = genUuid();
      if (!draftRef.current.photos) draftRef.current.photos = {};
    }
  };

  // Build the form snapshot. For flat photo slots (records carrying a `field`)
  // force the field to match the buffered state, so a crash right after capture
  // can't lose the link. Nested checklist files carry no `field` — their
  // placeholder already lives inside formData via handleChecklistChange.
  const snapshotFormData = () => {
    const fd = { ...formDataRef.current };
    const files = draftRef.current.photos || {};
    Object.keys(files).forEach((key) => {
      const rec = files[key];
      if (rec && rec.field) {
        fd[rec.field] = rec.s3Key ? rec.s3Key : OFFLINE_PHOTO_PREFIX + key;
      }
    });
    return fd;
  };

  const persistDraft = async (status) => {
    ensureDraftId();
    const record = {
      id: draftRef.current.id,
      propertyId: String(id),
      clientUuid: draftRef.current.clientUuid,
      status,
      formData: snapshotFormData(),
      photoRotations: photoRotationsRef.current || {},
      locations: locationsRef.current || [],
      photos: draftRef.current.photos || {},
      attempts: 0,
      permanent: false,
      lastError: null,
    };
    await saveDraft(record);
    draftActiveRef.current = true;
  };

  // Passed to <PhotoUpload> and <FileUpload>: buffer a file selected while
  // offline. `opts.field` (photo slots) keys the buffer by that field; checklist
  // files omit it and get a generated key. `opts.folder` is the S3 destination.
  // Throws if local storage is unavailable/full so the capture is aborted.
  const handleOfflineSave = useCallback(
    async (file, opts = {}) => {
      ensureDraftId();
      const { field, folder } = opts;
      const key = field || `cf_${genUuid()}`;
      const prev = draftRef.current.photos[key];
      draftRef.current.photos[key] = {
        blob: file,
        fileName: file.name,
        mimeType: file.type,
        folder: folder || `uploads/images/properties/${id}/reports/mir/`,
        field: field || null,
      };
      try {
        await persistDraft("draft");
      } catch (err) {
        // Roll back the in-memory buffer so it matches what was actually saved.
        if (prev) draftRef.current.photos[key] = prev;
        else delete draftRef.current.photos[key];
        throw err;
      }
      return OFFLINE_PHOTO_PREFIX + key;
    },
    [id] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Queue the whole report for background sync (offline submit, or online submit
  // that still has photos buffered / failed on the network).
  const queueForSync = async () => {
    await persistDraft("queued");
    toast.info(
      "Saved. This report will upload automatically when you’re back online."
    );
    triggerSync();
    navigate(`/properties/${id}/monthly-inspections`);
  };

  // Resume a saved offline draft for this property (called after prefill load).
  const restoreDraft = async () => {
    try {
      const saved = await getResumableDraftByProperty(id);
      if (!saved) return;
      draftRef.current = {
        id: saved.id,
        clientUuid: saved.clientUuid,
        photos: saved.photos || {},
      };
      draftActiveRef.current = true;
      if (saved.formData) setFormData(saved.formData);
      if (saved.photoRotations) setPhotoRotations(saved.photoRotations);
      if (saved.locations && saved.locations.length)
        setLocations(saved.locations);
      // Rebuild previews from the buffered photo Blobs (records with a `field`)
      // for the lightbox/gallery. Checklist files show a "Saved offline" state.
      const urls = {};
      Object.values(saved.photos || {}).forEach((rec) => {
        if (rec && rec.blob && rec.field) {
          const url = URL.createObjectURL(rec.blob);
          objectUrlsRef.current[rec.field] = url;
          urls[rec.field] = url;
        }
      });
      if (Object.keys(urls).length) {
        setPhotoPreviewUrls((p) => ({ ...p, ...urls }));
      }
      toast.info("Resumed your saved offline inspection report.");
    } catch (e) {
      console.warn("[MIR] restore draft failed:", e && e.message);
    }
  };

  // Debounced autosave while a draft is active and still being edited.
  useEffect(() => {
    if (!draftActiveRef.current) return undefined;
    const t = setTimeout(() => {
      persistDraft("draft").catch(() => {});
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, photoRotations, locations]);

  // Revoke restored-preview object URLs on unmount.
  useEffect(
    () => () => {
      Object.values(objectUrlsRef.current).forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {
          /* no-op */
        }
      });
    },
    []
  );

  // Months data
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  // Mock data generation
  const generateMockProperty = () => ({
    id: parseInt(id) || 1,
    property_id: "PROP001",
    name: "Sample Property",
    address_1: "123 Sample Street",
    address_2: "Apt 4B",
    address_3: "Building A",
    postcode: "SW1A 1AA",
    town: "London",
    date_live: "2023-01-15",
    client: "Sample Client Ltd",
    client_contact: "John Smith",
    client_contact_short: "John",
    account_manager: "Jane Doe",
    account_manager_email: "jane.doe@example.com",
    guardians_number: 2,
    savings_made: "£500",
    months_under_management: 12,
  });

  // Load data - Always use live data for Monthly Inspection Reports
  useEffect(() => {
    loadData();
  }, [id]); // Removed useMockData dependency to ensure live data only

  const loadData = async () => {
    setLoading(true);
    console.log("🔍 [FRONTEND] Starting loadData for property:", id);

    // Removed health check API call as requested

    // Check authentication
    const token = localStorage.getItem("token");
    console.log("🔍 [FRONTEND] Auth token exists:", !!token);
    if (!token) {
      console.error("🔍 [FRONTEND] No authentication token found");
      toast.error("Please log in to access this page");
      return;
    }

    try {
      // Always use live data for Monthly Inspection Reports
      console.log("🔍 [FRONTEND] Calling getFormData for property:", id);

        // Test if backend is accessible
        let response;
        try {
          console.log(
            "🔍 [FRONTEND] Making API call to:",
            `/monthlyinspectionreports/properties/${id}/form-data`
          );

          // First test with a simple GET request
          const testUrl = `http://localhost:3001/api/monthlyinspectionreports/properties/${id}/form-data`;
          console.log("🔍 [FRONTEND] Testing direct URL:", testUrl);

          response = await propertyMonthlyInspectionsService.getFormData(id);
          console.log("🔍 [FRONTEND] API Response:", response);
        } catch (apiError) {
          console.error("🔍 [FRONTEND] API Error:", apiError);
          console.error("🔍 [FRONTEND] Error details:", {
            message: apiError.message,
            status: apiError.response?.status,
            data: apiError.response?.data,
            config: apiError.config,
          });

          // Try to get more details about the error
          if (apiError.response) {
            console.error(
              "🔍 [FRONTEND] Response status:",
              apiError.response.status
            );
            console.error(
              "🔍 [FRONTEND] Response headers:",
              apiError.response.headers
            );
            console.error(
              "🔍 [FRONTEND] Response data:",
              apiError.response.data
            );
          }

          throw apiError;
        }

        if (
          response.data &&
          response.data.data &&
          response.data.data.property
        ) {
          setProperty(response.data.data.property);

          // Check if this is a Metropolitan Housing (MTVH) property
          const isMTVH = response.data.data.isMetropolitanHousing || false;
          setIsMetropolitanHousing(isMTVH);

          // Store ticket types for MTVH checklist
          if (response.data.data.ticketTypes) {
            setTicketTypes(response.data.data.ticketTypes);
          }

          // Store monthly inspection responses for MTVH notes dropdown
          if (response.data.data.monthlyInspectionResponses) {
            setMonthlyInspectionResponses(response.data.data.monthlyInspectionResponses);
          }

          // Pre-fill form with property data from API response
          setFormData((prev) => ({
            ...prev,
            // Use API data for available fields
            property_id_name: response.data.data.property.property_id || "",
            address_1: response.data.data.property.address_1 || "",
            address_2: response.data.data.property.address_2 || "",
            address_3: response.data.data.property.address_3 || "",
            postcode: response.data.data.property.postcode || "",
            town: response.data.data.property.town || "",
            // Auto-populate date_live from property data
            date_live: response.data.data.property.date_live || null,
            // Auto-populate lease_start_date from property data - convert to d/m/Y format
            lease_start_date: response.data.data.property.lease_start_date
              ? (response.data.data.property.lease_start_date.includes("/")
                  ? response.data.data.property.lease_start_date
                  : new Date(response.data.data.property.lease_start_date).toLocaleDateString("en-GB"))
              : "",
            // Auto-populate fields from property and client data
            client: response.data.data.client || "",
            client_contact: response.data.data.client_contact || "",
            client_contact_short: response.data.data.client_contact_short || "",
            account_manager: response.data.data.account_manager || "",
            account_manager_email: response.data.data.account_manager_email || "",
            guardians_number: response.data.data.guardians_number || 0,
            savings_made: response.data.data.savings_made || "",
            months_under_management: response.data.data.months_under_management || 0,
            // Use current year and month from API
            year: response.data.data.currentYear || new Date().getFullYear(),
            month: response.data.data.currentMonth || new Date().getMonth() + 1,
            // For MTVH properties, use "Bedroom" instead of "Living Space"
            image_2_name: isMTVH ? "Bedroom" : "Living Space",
            // Pre-populate default meters - matching Laravel behavior exactly
            // Both MTVH and Standard properties get Gas and Electricity (2 meters)
            // Meters Photos section is separate (image_5 through image_8 fields)
            reading_fields: ['Reading Gas', 'Reading Electricity'],
            reading_fields_names: ['Gas', 'Electricity'],
            reading_fields_serials: ['', ''],
          }));

          // After prefill, resume any saved offline draft for this property so a
          // report captured before a tab close/refresh is restored intact.
          await restoreDraft();
        } else {
          console.error("Invalid response structure:", response);
          toast.error("Invalid response from server - please try again");
          throw new Error("Invalid response structure from server");
        }
    } catch (error) {
      console.error("❌ [FRONTEND] Error loading property data:", error);
      toast.error("Failed to load property data from live database. Please check your connection and try again.");
      // No fallback to mock data - require live data for Monthly Inspection Reports
    } finally {
      setLoading(false);
    }
  };

  // Convert YYYY-MM-DD (from date input) to d/m/Y format (Laravel format)
  const convertToLaravelDateFormat = (dateString) => {
    if (!dateString) return "";
    // If already in d/m/Y format, return as is
    if (dateString.includes("/") && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }
    // Convert from YYYY-MM-DD (ISO format from date input) to d/m/Y
    if (dateString.includes("-")) {
      const [year, month, day] = dateString.split("-");
      return `${day}/${month}/${year}`;
    }
    return dateString;
  };

  // Convert d/m/Y format to YYYY-MM-DD (for date input)
  const convertToDateInputFormat = (dateString) => {
    if (!dateString) return "";
    // If already in YYYY-MM-DD format, return as is
    if (dateString.includes("-") && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Handle d/m/Y format (Laravel format)
    if (dateString.includes("/")) {
      const parts = dateString.split("/");
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    return "";
  };

  // Handle form field changes
  const handleInputChange = (field, value) => {
    // Convert date fields from YYYY-MM-DD to d/m/Y format when storing
    if (field === "inspection_date" || field === "lease_start_date") {
      value = convertToLaravelDateFormat(value);
    }
    
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle date change from native date picker (YYYY-MM-DD format)
  const handleDateChange = (field, e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      // Convert from YYYY-MM-DD to d/m/Y format (Laravel format)
      const [year, month, day] = dateValue.split("-");
      const formattedDate = `${day}/${month}/${year}`;
      setFormData((prev) => ({
        ...prev,
        [field]: formattedDate,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Open date picker
  const openDatePicker = (fieldName, e) => {
    e?.preventDefault();
    let dateInputRef;
    if (fieldName === "inspection_date") {
      dateInputRef = inspectionDateInputRef;
    } else if (fieldName === "lease_start_date") {
      dateInputRef = leaseStartDateInputRef;
    }

    if (dateInputRef?.current) {
      setTimeout(() => {
        try {
          if (dateInputRef.current.showPicker) {
            dateInputRef.current.showPicker();
          } else {
            dateInputRef.current.click();
          }
        } catch (error) {
          dateInputRef.current.click();
        }
      }, 0);
    }
  };

  // Handle photo upload - memoized to prevent infinite loops
  const handlePhotoUpload = useCallback((field, file, name = "") => {
    // Removal: also drop any buffered offline Blob + restored preview URL so a
    // removed photo is not re-synced later.
    if (file === null) {
      if (draftRef.current.photos && draftRef.current.photos[field]) {
        delete draftRef.current.photos[field];
      }
      if (objectUrlsRef.current[field]) {
        try {
          URL.revokeObjectURL(objectUrlsRef.current[field]);
        } catch (_) {
          /* no-op */
        }
        delete objectUrlsRef.current[field];
      }
      setPhotoPreviewUrls((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    setFormData((prev) => ({
      ...prev,
      [field]: file,
      [`${field}_name`]: name,
    }));
  }, []); // Empty dependency array - function doesn't depend on any props/state

  // Handle meter addition
  const handleAddMeter = () => {
    if (newMeterType.trim()) {
      setFormData((prev) => ({
        ...prev,
        reading_fields: [...prev.reading_fields, ""],
        reading_fields_names: [
          ...prev.reading_fields_names,
          newMeterType.trim(),
        ],
        reading_fields_serials: [...prev.reading_fields_serials, ""],
      }));
      setNewMeterType("");
    }
  };

  // Handle meter removal
  const handleRemoveMeter = (index) => {
    setFormData((prev) => ({
      ...prev,
      reading_fields: prev.reading_fields.filter((_, i) => i !== index),
      reading_fields_names: prev.reading_fields_names.filter(
        (_, i) => i !== index
      ),
      reading_fields_serials: prev.reading_fields_serials.filter(
        (_, i) => i !== index
      ),
    }));
  };

  // Add the next available meter photo slot (up to the pool of ten). Seeds a
  // default name so the required Name field isn't left blank.
  const handleAddMeterPhoto = () => {
    const next = METER_PHOTO_POOL.find((f) => !meterSlots.includes(f));
    if (!next) return; // pool exhausted (10 slots)
    setMeterSlots((prev) => (prev.includes(next) ? prev : [...prev, next]));
    setFormData((prev) => ({
      ...prev,
      [`${next}_name`]:
        prev[`${next}_name`] ||
        METER_DEFAULT_NAMES[next] ||
        `Meter ${meterSlots.length + 1}`,
    }));
  };

  // Remove a meter photo slot: hide it and clear its image + name.
  const handleRemoveMeterPhoto = (field) => {
    setMeterSlots((prev) => prev.filter((f) => f !== field));
    handlePhotoUpload(field, null, "");
  };

  // Handle meter field change
  // Oct 2026+: one reading per registered meter, keyed by meter id.
  const handleMeterReadingChange = (meterId, value) => {
    setFormData((prev) => ({
      ...prev,
      meter_readings: { ...(prev.meter_readings || {}), [meterId]: value },
    }));
  };

  const handleMeterChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  // Handle location addition - format as "Communal area - {name}" (matches Laravel)
  const handleAddLocation = () => {
    if (newLocation.trim()) {
      const formattedLocation = `Communal area - ${newLocation.trim()}`;
      setLocations((prev) => [...prev, formattedLocation]);
      setNewLocation("");
    }
  };

  // Handle location removal
  const handleRemoveLocation = (locationToRemove) => {
    setLocations((prev) => prev.filter((loc) => loc !== locationToRemove));
  };

  // Handle checklist change - supports nested location structure
  const handleChecklistChange = (section, item, field, value) => {
    // Handle location items which have nested structure: location[locationName][itemName]
    if (section === 'location' && item.includes('.')) {
      const [locationName, itemName] = item.split('.');
      setFormData((prev) => ({
        ...prev,
        location: {
          ...prev.location,
          [locationName]: {
            ...prev.location?.[locationName],
            [itemName]: {
              ...prev.location?.[locationName]?.[itemName],
              [field]: value,
            },
          },
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [item]: {
            ...prev[section]?.[item],
            [field]: value,
          },
        },
      }));
    }
  };

  // Handle signature
  const handleSignatureChange = (signatureData) => {
    setFormData((prev) => ({
      ...prev,
      e_sign: signatureData,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Explicit signature validation for non-MTVH properties (matching Laravel behavior)
    // This is a critical validation that must happen before other validations
    if (!isMetropolitanHousing) {
      if (!formData.e_sign || formData.e_sign === '' || formData.e_sign === 'null' ||
          (typeof formData.e_sign === 'string' && formData.e_sign.trim() === '')) {
        alert('You have to sign! The field is blank!');
        return;
      }
    }

    // Validate form data before submission (matching Laravel behavior)
    // Pass isMetropolitanHousing flag to skip signature validation for MTVH properties
    if (
      !validateAndShowErrors(formData, {
        isMetropolitanHousing,
        meters: useNewMeterRegister ? propertyMeters : [],
        noMetersConfirmed,
      })
    ) {
      return; // Stop submission if validation fails
    }
    
    const hasBufferedPhotos =
      Object.keys(draftRef.current.photos || {}).length > 0;
    const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;

    setSubmitting(true);

    // Offline, or photos still buffered locally: queue for background sync. The
    // sync engine uploads the photos first, then submits the report.
    if (!isOnline || hasBufferedPhotos) {
      try {
        await queueForSync();
      } catch (err) {
        setSubmitting(false);
        toast.error(
          "Could not save this report for offline upload: " +
            (err?.message || "storage unavailable")
        );
      }
      return;
    }

    try {
      // Merge pending rotations into the submit payload. Backend rotates the
      // S3 file in place and strips these fields before DB insert.
      const submitData = { ...formData };
      Object.entries(photoRotations).forEach(([field, degrees]) => {
        if (degrees) submitData[`${field}_rotate`] = degrees;
      });

      await propertyMonthlyInspectionsService.createMonthlyInspectionReport(
        id,
        submitData
      );
      // Online submit succeeded — clear any stale draft for this property.
      if (draftActiveRef.current) {
        await deleteDraft(draftRef.current.id).catch(() => {});
        draftActiveRef.current = false;
      }
      setPhotoRotations({});
      toast.success("Monthly inspection report created successfully!");
      navigate(`/properties/${id}/monthly-inspections`);
    } catch (error) {
      console.error("Error creating monthly inspection report:", error);
      // Network failure (no server response) → fall back to the offline queue
      // instead of losing the report.
      if (!error?.response) {
        try {
          await queueForSync();
          return;
        } catch (_) {
          /* fall through to error toast */
        }
      }
      toast.error("Failed to create monthly inspection report");
      setSubmitting(false);
    }
  };

  // Inspection photo slot ordering — left-to-right, top-to-bottom across the
  // 4 named columns. Standard layout = 4 single slots; MTVH = 4 columns × 3.
  const inspectionPhotoFields = isMetropolitanHousing
    ? [
        'image_1', 'image_1_a', 'image_1_b',
        'image_2', 'image_2_a', 'image_2_b',
        'image_3', 'image_3_a', 'image_3_b',
        'image_4', 'image_4_a', 'image_4_b',
      ]
    : ['image_1', 'image_2', 'image_3', 'image_4'];

  // Resolve a slot's image URL. On the create screen there is no presigned URL
  // yet, so the local data-URL preview (if any) is the best source. Falls back
  // to the backend S3 proxy when only the key is known.
  const resolveSlotUrl = (field) => {
    const preview = photoPreviewUrls[field];
    if (preview) return preview;
    const key = formData[field];
    if (!key || typeof key !== 'string') return null;
    if (key.startsWith('http')) return key;
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    return `${apiBase}/api/s3/file/${key}`;
  };

  const inspectionGallerySlides = inspectionPhotoFields
    .map((field) => {
      const src = resolveSlotUrl(field);
      if (!src) return null;
      const baseField = field.replace(/_[ab]$/, '');
      const categoryName = formData[`${baseField}_name`] || '';
      const variantSuffix = field.endsWith('_a') ? ' (2)' : field.endsWith('_b') ? ' (3)' : '';
      return { field, src, title: `${categoryName}${variantSuffix}` };
    })
    .filter(Boolean);

  const openInspectionGallery = (field) => {
    const idx = inspectionGallerySlides.findIndex((s) => s.field === field);
    if (idx >= 0) {
      setGalleryIndex(idx);
      setGalleryOpen(true);
    }
  };

  const inspectionGalleryRotations = inspectionGallerySlides.reduce((acc, slide, i) => {
    const r = photoRotations[slide.field];
    if (r) acc[i] = r;
    return acc;
  }, {});

  const handleInspectionGalleryRotate = (slideIdx) => {
    const slide = inspectionGallerySlides[slideIdx];
    if (slide) rotatePhotoField(slide.field);
  };

  // Which meter scheme this report uses, decided by its reporting period
  // (Oct 2026 onwards = the property register). See utils/meterCutover.js.
  const useNewMeterRegister = usesNewMeters(formData);

  // Labels for the new per-meter photo slots: type + serial, so two meters
  // of the same type stay distinguishable in the gallery and the PDF.
  const meterPhotoLabels = useNewMeterRegister
    ? propertyMeters.reduce((acc, meter) => {
        acc['meter_photo_' + meter.id] =
          meter.meter_type + ' \u2014 ' + meter.serial_number;
        return acc;
      }, {})
    : {};

  // Meter photos. Legacy reports use the fixed image_5..image_14 slots;
  // Oct 2026+ reports get one slot per registered meter.
  // True once we know the register is empty AND the chosen period needs it.
  // Recomputed as the month/year selects change, so switching to a post-
  // cutover period warns immediately rather than at submit.
  const meterRegisterMissing =
    metersLoaded &&
    useNewMeterRegister &&
    propertyMeters.length === 0 &&
    !noMetersConfirmed;

  useEffect(() => {
    if (meterRegisterMissing) {
      toast.error(
        'No meters are set up for this property. Add them on the property ' +
          'page before completing this inspection.'
      );
    }
  }, [meterRegisterMissing]);

  const meterPhotoFields = useNewMeterRegister
    ? propertyMeters.map((meter) => 'meter_photo_' + meter.id)
    : meterSlots;
  const meterGallerySlides = meterPhotoFields
    .map((field) => {
      const src = resolveSlotUrl(field);
      if (!src) return null;
      return {
        field,
        src,
        title: meterPhotoLabels[field] || formData[`${field}_name`] || '',
      };
    })
    .filter(Boolean);

  const openMeterGallery = (field) => {
    const idx = meterGallerySlides.findIndex((s) => s.field === field);
    if (idx >= 0) {
      setMeterGalleryIndex(idx);
      setMeterGalleryOpen(true);
    }
  };

  const meterGalleryRotations = meterGallerySlides.reduce((acc, slide, i) => {
    const r = photoRotations[slide.field];
    if (r) acc[i] = r;
    return acc;
  }, {});

  const handleMeterGalleryRotate = (slideIdx) => {
    const slide = meterGallerySlides[slideIdx];
    if (slide) rotatePhotoField(slide.field);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          Loading property data...
        </div>
      </div>
    );
  }

  // No meter register, and this report would use one: stop here rather than
  // let the inspector complete and sign a report that cannot be saved.
  // Meters are deliberately not addable from this screen -- the property page
  // is the only place they are managed.
  if (
    metersLoaded &&
    usesNewMeters(formData) &&
    propertyMeters.length === 0 &&
    !noMetersConfirmed
  ) {
    return (
      <div className={styles.container}>
        <div className={styles.breadcrumbs}>
          <Link to="/" className={styles.breadcrumbLink}>Dashboard</Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <Link to="/properties" className={styles.breadcrumbLink}>Properties</Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <Link to={`/properties/${id}`} className={styles.breadcrumbLink}>
            {property?.name || 'Property'}
          </Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span>Create Monthly Inspection Report</span>
        </div>

        <div
          style={{
            margin: '24px auto',
            maxWidth: '640px',
            padding: '24px',
            border: '1px solid #ebccd1',
            borderRadius: '4px',
            background: '#f2dede',
            color: '#a94442',
          }}
        >
          <h3 style={{ marginTop: 0 }}>No meters set up for this property</h3>
          <p>
            Monthly inspections now take readings from the property&rsquo;s meter
            register, and this property has none recorded yet. Add each meter
            &mdash; type, serial number and location &mdash; on the property page,
            then start the inspection.
          </p>
          <p style={{ marginBottom: 0 }}>
            <Link
              to={`/properties/${id}`}
              className="btn btn-primary"
            >
              Go to property page
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Debug Toggle - DISABLED for Monthly Inspection Reports to ensure live data usage */}
      <div className={styles.debugToggle} style={{ display: 'none' }}>
        <label>
          <input
            type="checkbox"
            checked={false}
            disabled={true}
            readOnly={true}
          />
          Use Mock Data (Disabled - Live Data Only)
        </label>
      </div>

      {meterRegisterMissing && (
        <div
          style={{
            margin: '0 0 20px',
            padding: '16px',
            border: '1px solid #ebccd1',
            borderRadius: '4px',
            background: '#f2dede',
            color: '#a94442',
          }}
        >
          <strong>
            This report cannot be saved: no meters are set up for this
            property.
          </strong>
          <div style={{ marginTop: '6px' }}>
            From October 2026, readings are taken against the property&rsquo;s
            meter register. Add each meter &mdash; type, serial number and
            location &mdash; on the property page, then return here.
          </div>
          <div style={{ marginTop: '10px' }}>
            <Link
              to={`/properties/${id}`}
              className="btn btn-primary"
              target="_blank"
            >
              Open property page
            </Link>
          </div>
        </div>
      )}
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <Link to="/" className={styles.breadcrumbLink}>Dashboard</Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <Link to="/properties" className={styles.breadcrumbLink}>Properties</Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <Link to={`/properties/${id}`} className={styles.breadcrumbLink}>{property?.name || 'Property'}</Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span>Create Monthly Inspection Report</span>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3 className={styles.panelTitle}>
                Create Monthly Inspection Report
              </h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit}>
                {/* Hidden fields */}
                <input type="hidden" name="type_id" value={formData.type_id} />
                <input
                  type="hidden"
                  name="property_id"
                  value={formData.property_id}
                />
                <input
                  type="hidden"
                  name="monthEnd"
                  value={formData.monthEnd}
                />
                <input
                  type="hidden"
                  name="created_by"
                  value={formData.created_by}
                />

                {/* Status */}
                <div className={styles.formSection}>
                  <div className={styles.row}>
                    <div className={styles.colSmOffset3}>
                      <div className={styles.formGroup}>
                        <label className={styles.controlLabel}>
                          Status <span className={styles.required}>*</span>
                        </label>
                        <div className={styles.colSm6}>
                          <select
                            name="status_id"
                            value={formData.status_id}
                            onChange={(e) =>
                              handleInputChange(
                                "status_id",
                                parseInt(e.target.value)
                              )
                            }
                            className={styles.formControl}
                            required
                          >
                            <option value={0}>Unpublished</option>
                            <option value={1}>Published</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Photos Section */}
                <div className={styles.reportSectionHeader}>Photos</div>

                {/* Photos grid - grouped by category so mobile layout keeps uploads with their name */}
                <div className={styles.row}>
                  <div className={styles.colMd3}>
                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Name <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm10}>
                        <input
                          type="text"
                          name="image_1_name"
                          value={formData.image_1_name || ""}
                          onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>
                    <PhotoUpload
                      label="Front"
                      fieldName="image_1"
                      onUpload={handlePhotoUpload}
                      existingImage={formData.image_1}
                      existingImageName={formData.image_1_name}
                      onPreviewClick={() => openInspectionGallery('image_1')}
                      rotation={photoRotations.image_1 || 0}
                      onRotate={() => rotatePhotoField('image_1')}
                      onPreviewReady={handlePhotoPreviewReady}
                      onOfflineSave={handleOfflineSave}
                    />
                    {isMetropolitanHousing && (
                      <PhotoUpload
                        label="Front (2)"
                        fieldName="image_1_a"
                        onUpload={handlePhotoUpload}
                        existingImage={formData.image_1_a}
                        onPreviewClick={() => openInspectionGallery('image_1_a')}
                        rotation={photoRotations.image_1_a || 0}
                        onRotate={() => rotatePhotoField('image_1_a')}
                        onPreviewReady={handlePhotoPreviewReady}
                        onOfflineSave={handleOfflineSave}
                      />
                    )}
                    {isMetropolitanHousing && (
                      <PhotoUpload
                        label="Front (3)"
                        fieldName="image_1_b"
                        onUpload={handlePhotoUpload}
                        existingImage={formData.image_1_b}
                        onPreviewClick={() => openInspectionGallery('image_1_b')}
                        rotation={photoRotations.image_1_b || 0}
                        onRotate={() => rotatePhotoField('image_1_b')}
                        onPreviewReady={handlePhotoPreviewReady}
                        onOfflineSave={handleOfflineSave}
                      />
                    )}
                  </div>
                  <div className={styles.colMd3}>
                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Name <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm10}>
                        <input
                          type="text"
                          name="image_2_name"
                          value={formData.image_2_name || ""}
                          onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>
                    <PhotoUpload
                      label={isMetropolitanHousing ? "Bedroom" : "Living Space"}
                      fieldName="image_2"
                      onUpload={handlePhotoUpload}
                      existingImage={formData.image_2}
                      existingImageName={formData.image_2_name}
                      onPreviewClick={() => openInspectionGallery('image_2')}
                      rotation={photoRotations.image_2 || 0}
                      onRotate={() => rotatePhotoField('image_2')}
                      onPreviewReady={handlePhotoPreviewReady}
                      onOfflineSave={handleOfflineSave}
                    />
                    {isMetropolitanHousing && (
                      <PhotoUpload
                        label={`${isMetropolitanHousing ? "Bedroom" : "Living Space"} (2)`}
                        fieldName="image_2_a"
                        onUpload={handlePhotoUpload}
                        existingImage={formData.image_2_a}
                        onPreviewClick={() => openInspectionGallery('image_2_a')}
                        rotation={photoRotations.image_2_a || 0}
                        onRotate={() => rotatePhotoField('image_2_a')}
                        onPreviewReady={handlePhotoPreviewReady}
                        onOfflineSave={handleOfflineSave}
                      />
                    )}
                    {isMetropolitanHousing && (
                      <PhotoUpload
                        label={`${isMetropolitanHousing ? "Bedroom" : "Living Space"} (3)`}
                        fieldName="image_2_b"
                        onUpload={handlePhotoUpload}
                        existingImage={formData.image_2_b}
                        onPreviewClick={() => openInspectionGallery('image_2_b')}
                        rotation={photoRotations.image_2_b || 0}
                        onRotate={() => rotatePhotoField('image_2_b')}
                        onPreviewReady={handlePhotoPreviewReady}
                        onOfflineSave={handleOfflineSave}
                      />
                    )}
                  </div>
                  <div className={styles.colMd3}>
                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Name <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm10}>
                        <input
                          type="text"
                          name="image_3_name"
                          value={formData.image_3_name || ""}
                          onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>
                    <PhotoUpload
                      label="Kitchen/Bathroom"
                      fieldName="image_3"
                      onUpload={handlePhotoUpload}
                      existingImage={formData.image_3}
                      existingImageName={formData.image_3_name}
                      onPreviewClick={() => openInspectionGallery('image_3')}
                      rotation={photoRotations.image_3 || 0}
                      onRotate={() => rotatePhotoField('image_3')}
                      onPreviewReady={handlePhotoPreviewReady}
                      onOfflineSave={handleOfflineSave}
                    />
                    {isMetropolitanHousing && (
                      <PhotoUpload
                        label="Kitchen/Bathroom (2)"
                        fieldName="image_3_a"
                        onUpload={handlePhotoUpload}
                        existingImage={formData.image_3_a}
                        onPreviewClick={() => openInspectionGallery('image_3_a')}
                        rotation={photoRotations.image_3_a || 0}
                        onRotate={() => rotatePhotoField('image_3_a')}
                        onPreviewReady={handlePhotoPreviewReady}
                        onOfflineSave={handleOfflineSave}
                      />
                    )}
                    {isMetropolitanHousing && (
                      <PhotoUpload
                        label="Kitchen/Bathroom (3)"
                        fieldName="image_3_b"
                        onUpload={handlePhotoUpload}
                        existingImage={formData.image_3_b}
                        onPreviewClick={() => openInspectionGallery('image_3_b')}
                        rotation={photoRotations.image_3_b || 0}
                        onRotate={() => rotatePhotoField('image_3_b')}
                        onPreviewReady={handlePhotoPreviewReady}
                        onOfflineSave={handleOfflineSave}
                      />
                    )}
                  </div>
                  <div className={styles.colMd3}>
                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Name <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm10}>
                        <input
                          type="text"
                          name="image_4_name"
                          value={formData.image_4_name || ""}
                          onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>
                    <PhotoUpload
                      label="Communal Area"
                      fieldName="image_4"
                      onUpload={handlePhotoUpload}
                      existingImage={formData.image_4}
                      existingImageName={formData.image_4_name}
                      onPreviewClick={() => openInspectionGallery('image_4')}
                      rotation={photoRotations.image_4 || 0}
                      onRotate={() => rotatePhotoField('image_4')}
                      onPreviewReady={handlePhotoPreviewReady}
                      onOfflineSave={handleOfflineSave}
                    />
                    {isMetropolitanHousing && (
                      <PhotoUpload
                        label="Communal Area (2)"
                        fieldName="image_4_a"
                        onUpload={handlePhotoUpload}
                        existingImage={formData.image_4_a}
                        onPreviewClick={() => openInspectionGallery('image_4_a')}
                        rotation={photoRotations.image_4_a || 0}
                        onRotate={() => rotatePhotoField('image_4_a')}
                        onPreviewReady={handlePhotoPreviewReady}
                        onOfflineSave={handleOfflineSave}
                      />
                    )}
                    {isMetropolitanHousing && (
                      <PhotoUpload
                        label="Communal Area (3)"
                        fieldName="image_4_b"
                        onUpload={handlePhotoUpload}
                        existingImage={formData.image_4_b}
                        onPreviewClick={() => openInspectionGallery('image_4_b')}
                        rotation={photoRotations.image_4_b || 0}
                        onRotate={() => rotatePhotoField('image_4_b')}
                        onPreviewReady={handlePhotoPreviewReady}
                        onOfflineSave={handleOfflineSave}
                      />
                    )}
                  </div>
                </div>

                {/* Brief Section */}
                <div className={styles.reportSectionHeader}>Brief</div>
                <div className={styles.formGroup}>
                  <label className={styles.controlLabel}>
                    Brief Overview <span className={styles.required}>*</span>
                  </label>
                  <div className={styles.colSm10}>
                    <textarea
                      name="brief_overview"
                      value={formData.brief_overview}
                      onChange={(e) =>
                        handleInputChange("brief_overview", e.target.value)
                      }
                      className={styles.formControl}
                      rows={6}
                      required
                    />
                  </div>
                </div>

                {/* Dates Section */}
                <div className={styles.reportSectionHeader}>Dates</div>
                <div className={styles.row}>
                  <div className={styles.colXs6}>
                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Inspection Date{" "}
                        <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6} style={{ position: "relative" }}>
                        <input
                          type="text"
                          name="inspection_date"
                          placeholder="dd/mm/yyyy"
                          value={formData.inspection_date}
                          readOnly
                          onClick={(e) => openDatePicker("inspection_date", e)}
                          className={styles.formControl}
                          required
                          style={{ paddingRight: "35px", cursor: "pointer" }}
                        />
                        {/* Hidden date input for native date picker */}
                        <input
                          ref={inspectionDateInputRef}
                          type="date"
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: "100%",
                            height: "100%",
                            opacity: 0,
                            cursor: "pointer",
                            zIndex: 10,
                            fontSize: "16px",
                            padding: "6px 35px 6px 10px",
                            margin: 0,
                            border: "none",
                            background: "transparent",
                            boxSizing: "border-box",
                          }}
                          value={convertToDateInputFormat(formData.inspection_date)}
                          onChange={(e) => handleDateChange("inspection_date", e)}
                        />
                        <span
                          className="glyphicon glyphicon-calendar"
                          onClick={(e) => openDatePicker("inspection_date", e)}
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#999",
                            cursor: "pointer",
                            pointerEvents: "auto",
                            zIndex: 11,
                            fontSize: "14px",
                          }}
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Year <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <input
                          type="number"
                          name="year"
                          value={formData.year}
                          onChange={(e) =>
                            handleInputChange("year", parseInt(e.target.value))
                          }
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles.colXs6}>
                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Month <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <select
                          name="month"
                          value={formData.month}
                          onChange={(e) =>
                            handleInputChange("month", parseInt(e.target.value))
                          }
                          className={styles.formControl}
                          required
                        >
                          {months.map((month) => (
                            <option key={month.value} value={month.value}>
                              {month.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Lease Start Date{" "}
                        <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6} style={{ position: "relative" }}>
                        <input
                          type="text"
                          name="lease_start_date"
                          placeholder="dd/mm/yyyy"
                          value={formData.lease_start_date}
                          readOnly
                          onClick={(e) => openDatePicker("lease_start_date", e)}
                          className={styles.formControl}
                          required
                          style={{ paddingRight: "35px", cursor: "pointer" }}
                        />
                        {/* Hidden date input for native date picker */}
                        <input
                          ref={leaseStartDateInputRef}
                          type="date"
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: "100%",
                            height: "100%",
                            opacity: 0,
                            cursor: "pointer",
                            zIndex: 10,
                            fontSize: "16px",
                            padding: "6px 35px 6px 10px",
                            margin: 0,
                            border: "none",
                            background: "transparent",
                            boxSizing: "border-box",
                          }}
                          value={convertToDateInputFormat(formData.lease_start_date)}
                          onChange={(e) => handleDateChange("lease_start_date", e)}
                        />
                        <span
                          className="glyphicon glyphicon-calendar"
                          onClick={(e) => openDatePicker("lease_start_date", e)}
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#999",
                            cursor: "pointer",
                            pointerEvents: "auto",
                            zIndex: 11,
                            fontSize: "14px",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Meters Section */}
                <div className={styles.reportSectionHeader}>Meters</div>

                {useNewMeterRegister ? (
                  <>
                  {propertyMeters.length === 0 ? (
                    <p style={{ margin: "4px 0 16px", fontSize: "13px", color: "#a94442" }}>
                      No meters are set up for this property yet. Add them on the
                      property page before completing this inspection.
                    </p>
                  ) : (
                    <>
                    <p style={{ margin: "4px 0 10px", fontSize: "13px", color: "#6c757d" }}>
                      Meters come from the property record. Enter this month&rsquo;s
                      reading and upload a photo for each. To add or remove a meter,
                      use the property page.
                    </p>
                    <div className={styles.row}>
                      {propertyMeters.map((meter) => {
                        const photoField = 'meter_photo_' + meter.id;
                        return (
                          <div className={styles.colMd3} key={meter.id}>
                            <div className={styles.formGroup}>
                              <label className={styles.controlLabel}>
                                {meter.meter_type}
                              </label>
                              <div style={{ fontSize: "12px", color: "#6c757d", marginBottom: "8px" }}>
                                Serial {meter.serial_number}
                                <br />
                                {meter.location}
                              </div>
                              <label className={styles.controlLabel}>
                                Reading <span className={styles.required}>*</span>
                              </label>
                              <input
                                type="text"
                                className={styles.formControl}
                                maxLength={255}
                                value={formData.meter_readings?.[meter.id] || ''}
                                onChange={(e) =>
                                  handleMeterReadingChange(meter.id, e.target.value)
                                }
                                required
                              />
                            </div>
                            <PhotoUpload
                              label={meterPhotoLabels[photoField] || meter.meter_type}
                              fieldName={photoField}
                              onUpload={handlePhotoUpload}
                              existingImage={formData[photoField]}
                              onPreviewClick={() => openMeterGallery(photoField)}
                              rotation={photoRotations[photoField] || 0}
                              onRotate={() => rotatePhotoField(photoField)}
                              onPreviewReady={handlePhotoPreviewReady}
                              onOfflineSave={handleOfflineSave}
                            />
                          </div>
                        );
                      })}
                    </div>
                    </>
                  )}
                  </>
                ) : (
                  <>
                {/* Type new reading type - positioned at top right like Laravel */}
                <div className={styles.row} style={{ marginBottom: "15px", justifyContent: "flex-end" }}>
                  <div className={styles.colSm3}>
                    <div className={styles.entry}>
                      <input
                        id="new_field_input"
                        className={styles.formControl}
                        type="text"
                        placeholder="Type new reading type"
                        value={newMeterType}
                        onChange={(e) => setNewMeterType(e.target.value)}
                      />
                      <button
                        id="new_field_button"
                        className={styles.btnAdd}
                        type="button"
                        onClick={handleAddMeter}
                      >
                        <span className={styles.glyphicon}>+</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div id="js-monthly-inspection-report-meters-form">
                  {formData.reading_fields.map((reading, index) => (
                    <div key={index} className={styles.row}>
                      <input
                        type="hidden"
                        name={`reading_fields_names[${index}]`}
                        value={formData.reading_fields_names[index]}
                      />
                      <div className={styles.colSm1}>
                        <button
                          type="button"
                          onClick={() => handleRemoveMeter(index)}
                          className={styles.btnRemove}
                        >
                          Delete
                        </button>
                      </div>
                      <div className={styles.colSm5}>
                        <div className={styles.formGroup}>
                          <label className={styles.controlLabel}>
                            Reading {formData.reading_fields_names[index]}{" "}
                            <span className={styles.required}>*</span>
                          </label>
                          <div className={styles.colSm6}>
                            <input
                              type="text"
                              name={`reading_fields[${index}]`}
                              value={reading}
                              onChange={(e) =>
                                handleMeterChange(
                                  index,
                                  "reading_fields",
                                  e.target.value
                                )
                              }
                              className={styles.formControl}
                              required
                            />
                          </div>
                        </div>
                      </div>
                      <div className={styles.colSm6}>
                        <div className={styles.formGroup}>
                          <label className={styles.controlLabel}>
                            Reading {formData.reading_fields_names[index]}{" "}
                            Serial <span className={styles.required}>*</span>
                          </label>
                          <div className={styles.colSm6}>
                            <input
                              type="text"
                              name={`reading_fields_serials[${index}]`}
                              value={formData.reading_fields_serials[index]}
                              onChange={(e) =>
                                handleMeterChange(
                                  index,
                                  "reading_fields_serials",
                                  e.target.value
                                )
                              }
                              className={styles.formControl}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Meters Photos Section - shown for all clients, MTVH included */}
                <>
                <div className={styles.reportSectionHeader}>Meters Photos</div>
                <p style={{ margin: "4px 0 10px", fontSize: "13px", color: "#6c757d" }}>
                  You can add up to {METER_PHOTO_POOL.length} meter photos.
                </p>
                <div
                  className={styles.row}
                  style={{ marginBottom: "10px", justifyContent: "flex-end" }}
                >
                  <button
                    type="button"
                    className={styles.btnAdd}
                    onClick={handleAddMeterPhoto}
                    disabled={meterSlots.length >= METER_PHOTO_POOL.length}
                    title={
                      meterSlots.length >= METER_PHOTO_POOL.length
                        ? "Maximum of 10 meter photos reached"
                        : "Add another meter photo"
                    }
                  >
                    <span className={styles.glyphicon}>+</span> Add photo
                  </button>
                </div>
                <div className={styles.row}>
                  {meterSlots.map((field) => (
                    <div className={styles.colMd3} key={field}>
                      <div className={styles.formGroup}>
                        <label className={styles.controlLabel}>
                          Name <span className={styles.required}>*</span>
                          {meterSlots.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveMeterPhoto(field)}
                              className={styles.btnRemove}
                              style={{ marginLeft: "8px" }}
                            >
                              Delete
                            </button>
                          )}
                        </label>
                        <div className={styles.colSm10}>
                          <input
                            type="text"
                            name={`${field}_name`}
                            value={
                              formData[`${field}_name`] ||
                              METER_DEFAULT_NAMES[field] ||
                              ""
                            }
                            onChange={(e) =>
                              handleInputChange(e.target.name, e.target.value)
                            }
                            className={styles.formControl}
                            placeholder="Meter name"
                            required
                          />
                        </div>
                      </div>
                      <PhotoUpload
                        label={
                          formData[`${field}_name`] ||
                          METER_DEFAULT_NAMES[field] ||
                          "Meter"
                        }
                        fieldName={field}
                        onUpload={handlePhotoUpload}
                        existingImage={formData[field]}
                        existingImageName={formData[`${field}_name`]}
                        onPreviewClick={() => openMeterGallery(field)}
                        rotation={photoRotations[field] || 0}
                        onRotate={() => rotatePhotoField(field)}
                        onPreviewReady={handlePhotoPreviewReady}
                        onOfflineSave={handleOfflineSave}
                      />
                    </div>
                  ))}
                </div>
                </>
                  </>
                )}

                {/* Tickets Section - Hidden for MTVH properties */}
                {!isMetropolitanHousing && (
                  <>
                <div className={styles.row} style={{ marginTop: "30px" }}>
                  <div className={styles.colXs12}>
                    <div className={styles.reportSectionHeader}>Tickets</div>
                  </div>
                </div>
                <TicketsTable
                  propertyId={id}
                  month={formData.month}
                  year={formData.year}
                  onMonthYearChange={(newMonth, newYear) => {
                    setFormData(prev => ({
                      ...prev,
                      month: newMonth,
                      year: newYear
                    }));
                  }}
                />
                  </>
                )}

                {/* Other Fields Section */}
                <div className={styles.row}>
                  <div className={styles.colXs12}>
                    <div className={styles.reportSectionHeader}>Other</div>
                  </div>

                  <div className={styles.colXs6}>
                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Client <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <input
                          type="text"
                          name="client"
                          value={formData.client}
                          onChange={(e) =>
                            handleInputChange("client", e.target.value)
                          }
                          className={styles.formControl}
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Contact Person{" "}
                        <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <input
                          type="text"
                          name="client_contact"
                          value={formData.client_contact}
                          onChange={(e) =>
                            handleInputChange("client_contact", e.target.value)
                          }
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Property ID <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <input
                          type="text"
                          name="property_id_name"
                          value={formData.property_id_name}
                          onChange={(e) =>
                            handleInputChange(
                              "property_id_name",
                              e.target.value
                            )
                          }
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>Address 2</label>
                      <div className={styles.colSm6}>
                        <input
                          type="text"
                          name="address_2"
                          value={formData.address_2}
                          onChange={(e) =>
                            handleInputChange("address_2", e.target.value)
                          }
                          className={styles.formControl}
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Postcode <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <input
                          type="text"
                          name="postcode"
                          value={formData.postcode}
                          onChange={(e) =>
                            handleInputChange("postcode", e.target.value)
                          }
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Number of Guardians in Occupation{" "}
                        <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm3}>
                        <input
                          type="number"
                          name="guardians_number"
                          value={formData.guardians_number}
                          onChange={(e) =>
                            handleInputChange(
                              "guardians_number",
                              parseInt(e.target.value)
                            )
                          }
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles.colXs6}>
                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Account Manager{" "}
                        <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <input
                          type="text"
                          name="account_manager"
                          value={formData.account_manager}
                          onChange={(e) =>
                            handleInputChange("account_manager", e.target.value)
                          }
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Account Manager Email{" "}
                        <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <input
                          type="email"
                          name="account_manager_email"
                          value={formData.account_manager_email}
                          onChange={(e) =>
                            handleInputChange(
                              "account_manager_email",
                              e.target.value
                            )
                          }
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Contact First Name{" "}
                        <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <input
                          type="text"
                          name="client_contact_short"
                          value={formData.client_contact_short}
                          onChange={(e) =>
                            handleInputChange(
                              "client_contact_short",
                              e.target.value
                            )
                          }
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Address 1 <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <input
                          type="text"
                          name="address_1"
                          value={formData.address_1}
                          onChange={(e) =>
                            handleInputChange("address_1", e.target.value)
                          }
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>Address 3</label>
                      <div className={styles.colSm6}>
                        <input
                          type="text"
                          name="address_3"
                          value={formData.address_3}
                          onChange={(e) =>
                            handleInputChange("address_3", e.target.value)
                          }
                          className={styles.formControl}
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Town <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <input
                          type="text"
                          name="town"
                          value={formData.town}
                          onChange={(e) =>
                            handleInputChange("town", e.target.value)
                          }
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Savings Made <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <input
                          type="text"
                          name="savings_made"
                          value={formData.savings_made}
                          onChange={(e) =>
                            handleInputChange("savings_made", e.target.value)
                          }
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Amount of Months Since Lease Start Date{" "}
                        <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.colSm6}>
                        <input
                          type="number"
                          name="months_under_management"
                          value={formData.months_under_management}
                          onChange={(e) =>
                            handleInputChange(
                              "months_under_management",
                              parseInt(e.target.value)
                            )
                          }
                          className={styles.formControl}
                          required
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        Property Date Live
                      </label>
                      <div className={styles.colSm6}>
                        <label className={styles.controlLabel}>
                          {property?.date_live
                            ? new Date(property.date_live).toLocaleDateString(
                                "en-GB"
                              )
                            : "N/A"}
                        </label>
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>Today</label>
                      <div className={styles.colSm6}>
                        <label className={styles.controlLabel}>
                          {new Date().toLocaleDateString("en-GB")}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Checklist Section - Different layout for MTVH vs Standard */}
                <ChecklistGroup
                  title="Monthly Inspection Checklist"
                  sections={isMetropolitanHousing ? {
                    // MTVH simplified checklist - single item per category
                    external: {
                      title: "External",
                      items: ["External"],
                    },
                    location: {
                      title: "Communal Area",
                      items: ["Communal Area"],
                    },
                    kitchen: {
                      title: "Kitchen",
                      items: ["Kitchen"],
                    },
                    living_space: {
                      title: "Bedroom",
                      items: ["Bedroom"],
                    },
                    bathroom: {
                      title: "Bathroom",
                      items: ["Bathroom"],
                    },
                    other: {
                      title: "Other",
                      items: ["Other"],
                    },
                  } : {
                    // Standard detailed checklist
                    external: {
                      title: "External",
                      items: [
                        "Front Door",
                        "Back Door",
                        "Other Exit Door",
                        "External Walls",
                        "Windows",
                        "Garage/Shed",
                        "Lawn",
                        "Bin(s)",
                        "Exits Clear",
                        "Other",
                      ],
                    },
                    kitchen: {
                      title: "Kitchen",
                      items: [
                        "Door(s)",
                        "Wall",
                        "Ceiling",
                        "Window(s)",
                        "Floor",
                        "Light(s)",
                        "Washing Machine",
                        "Hot plate",
                        "Microwave",
                        "Bin(s)",
                        "Sink",
                        "Plug socket(s)",
                        "Fire extinguisher(s)",
                        "Fire blanket",
                        "Other",
                      ],
                    },
                    living_space: {
                      title: "Living Space",
                      items: [
                        "Door",
                        "Wall",
                        "Ceiling",
                        "Window(s)",
                        "Floor",
                        "Light(s)",
                        "Plug socket(s)",
                        "Fire extinguisher(s)",
                        "Fire blanket",
                        "Other",
                      ],
                    },
                    bathroom: {
                      title: "Bathroom",
                      items: [
                        "Door",
                        "Wall",
                        "Ceiling",
                        "Window",
                        "Floor",
                        "Light",
                        "Plug socket(s)",
                        "Water",
                        "Shower/cubicle",
                        "Bath",
                        "Sink",
                        "Extractor fan",
                        "Other",
                      ],
                    },
                    other: {
                      title: "Other",
                      items: [
                        "Emergency lighting",
                        "Alarms",
                        "Toilet",
                        "Roof",
                        "Stairwells - Clear",
                        "Landings - Clear",
                        "Plug socket(s)",
                      ],
                    },
                    health_and_safety: {
                      title: "Health and Safety",
                      items: [
                        "Unused water outlets run for 2 minutes",
                        "Obstacles",
                        "Electric voltage over 13 amps",
                        "Other",
                      ],
                    },
                  }}
                  locations={locations}
                  onLocationAdd={handleAddLocation}
                  onLocationRemove={handleRemoveLocation}
                  newLocation={newLocation}
                  setNewLocation={setNewLocation}
                  data={formData}
                  onChange={handleChecklistChange}
                  isMetropolitan={isMetropolitanHousing}
                  showLocations={!isMetropolitanHousing}
                  ticketTypes={ticketTypes}
                  monthlyInspectionResponses={monthlyInspectionResponses}
                  onOfflineSave={handleOfflineSave}
                />

                {/* Signature Section - Hidden for MTVH properties */}
                {!isMetropolitanHousing && (
                  <div className={styles.row}>
                    <div className={styles.colXs12}>
                      <div className={styles.eSignBox}>
                        <div>
                          <h3>Sign the report</h3>
                        </div>
                        <SignatureCanvas
                          onChange={handleSignatureChange}
                          value={formData.e_sign}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className={styles.formGroup}>
                  <div className={styles.colSm12}>
                    <button
                      type="submit"
                      className={styles.btnHead}
                      disabled={submitting || meterRegisterMissing}
                    >
                      {submitting ? "Creating..." : "Create"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Large image viewer — inspection photos */}
      <PhotoGallery
        open={galleryOpen}
        close={() => setGalleryOpen(false)}
        slides={inspectionGallerySlides}
        index={galleryIndex}
        rotations={inspectionGalleryRotations}
        onRotate={handleInspectionGalleryRotate}
      />

      {/* Large image viewer — meter photos (separate set) */}
      <PhotoGallery
        open={meterGalleryOpen}
        close={() => setMeterGalleryOpen(false)}
        slides={meterGallerySlides}
        index={meterGalleryIndex}
        rotations={meterGalleryRotations}
        onRotate={handleMeterGalleryRotate}
      />
    </div>
  );
};

export default MonthlyInspectionCreate;
